// public/js/utils/share-fields.js
// The asset fields a share link may expose, and how to read them.
//
// ONE list, used by both halves: the modal offers exactly these, and the
// Lambda accepts exactly these. A field that is not here cannot be shared,
// whatever a request asks for - which is the point, because a share link is
// readable by anyone it is forwarded to.
//
// It lives under public/js/utils so the backend can import it the same way
// ingest already imports rssi-level.js. Keep it dependency-free: it is
// loaded both in a browser and in the Lambda.

/**
 * @typedef {{id:string, label:string, group:string, sensitive?:boolean}} ShareField
 */

/** @type {ShareField[]} */
export const SHARE_FIELDS = [
  { id: 'label',      label: 'Name',        group: 'Identification' },
  { id: 'reference',  label: 'Reference',   group: 'Identification' },
  { id: 'serial',     label: 'Serial',      group: 'Identification' },
  { id: 'type',       label: 'Asset type',  group: 'Classification' },
  { id: 'status',     label: 'Status',      group: 'Classification' },
  // Offered, but it names the customer rather than the thing being shipped,
  // so the modal marks it and it is never on by default.
  { id: 'tenantName', label: 'Owner',       group: 'Classification', sensitive: true },
];

// The asset UUID is deliberately absent: it addresses our own API and tells
// a recipient nothing about the shipment.

const BY_ID = new Map(SHARE_FIELDS.map(f => [f.id, f]));

export const isShareableField = (id) => BY_ID.has(id);

/** Keep only known field ids, de-duplicated, in catalogue order. */
export function sanitiseFields(ids) {
  const want = new Set(Array.isArray(ids) ? ids.filter(i => typeof i === 'string') : []);
  return SHARE_FIELDS.filter(f => want.has(f.id)).map(f => f.id);
}

/** The value a link should show for one field, or null when there is none. */
export function shareFieldValue(asset, id) {
  if (!asset || !BY_ID.has(id)) return null;
  const v = asset[id];
  return (v === undefined || v === null || v === '') ? null : String(v);
}

/**
 * Resolve the chosen fields into what the public page renders.
 * @returns {{key:string, label:string, value:string}[]}
 */
export function resolveShareFields(asset, ids) {
  return sanitiseFields(ids)
    .map(id => ({ key: id, label: BY_ID.get(id).label, value: shareFieldValue(asset, id) }))
    .filter(f => f.value !== null);
}
